import { useState } from "react";
import { FiDollarSign, FiActivity, FiFileText } from "react-icons/fi";
import { toSigFigs } from "../../../utils/format";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import type { ChartData } from "chart.js";
import type { LoadImpactSummary, CategoryBreakdown } from "../domain/types";
import { getCategoryDisplayName, isHiddenCategory } from "../../model/domain/definitions";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type MetricTab = "economic" | "dalys" | "pdfs";

// ReCiPe 2016 monetization factors (must match orchestrator.py)
const DALY_VALUE = 177_040.57;
const PDF_VALUE = 17_927_450.0;

interface Props {
  summary: LoadImpactSummary;
}

const TAB_CONFIG: Record<
  MetricTab,
  {
    label: string;
    icon: typeof FiDollarSign;
    activeColor: string;
    hoverColor: string;
    unit: string;
    description: string;
  }
> = {
  economic: {
    label: "Economic",
    icon: FiDollarSign,
    activeColor: "text-economic-400 border-economic-400 bg-economic-400/10",
    hoverColor: "hover:text-economic-400 hover:bg-economic-400/5",
    unit: "USD",
    description:
      "Environmental damages broken down by impact category. All values represent increases in societal cost.",
  },
  dalys: {
    label: "Health",
    icon: FiActivity,
    activeColor: "text-health-400 border-health-400 bg-health-400/10",
    hoverColor: "hover:text-health-400 hover:bg-health-400/5",
    unit: "DALYs",
    description:
      "Health impacts — each category shows its contribution to disability-adjusted life years added.",
  },
  pdfs: {
    label: "Biodiversity",
    icon: FiFileText,
    activeColor: "text-ecosystem-400 border-ecosystem-400 bg-ecosystem-400/10",
    hoverColor: "hover:text-ecosystem-400 hover:bg-ecosystem-400/5",
    unit: "PDFs",
    description:
      "Ecosystem impacts — each category shows its contribution to species habitat loss added.",
  },
};

/** Extract the value for the active tab from a category breakdown.
 *  For economic: total monetized value = DALYs*DALY_VALUE + PDFs*PDF_VALUE + USD.
 *  This matches how the orchestrator computes total USD from all three endpoints. */
function getValue(b: CategoryBreakdown, tab: MetricTab): number {
  switch (tab) {
    case "economic":
      return b.DALYs * DALY_VALUE + b.PDFs * PDF_VALUE + b.USD;
    case "dalys":
      return b.DALYs;
    case "pdfs":
      return b.PDFs;
  }
}

function formatTableValue(val: number, tab: MetricTab): string {
  if (tab === "economic") {
    const abs = Math.abs(val);
    if (abs >= 1_000_000) return `$${toSigFigs(val / 1_000_000)}M`;
    if (abs >= 1_000) return `$${toSigFigs(val / 1_000)}K`;
    return `$${toSigFigs(val)}`;
  }
  return toSigFigs(val);
}

export function LoadImpactExplorer({ summary }: Props) {
  const [activeTab, setActiveTab] = useState<MetricTab>("economic");

  // Prefer the grid-supply breakdown (the full ReCiPe/USEtox set for a
  // grid-drawing load — air, soil & water); fall back to on-site NG for
  // btm_only portfolios with no grid residual.
  // Side-by-side per category: Consumption (grid imports + on-site NG) | RE
  // offset (avoided grid) | RE infrastructure | Net = consumption − offset + infra.
  // Totalizer rows ("… Total Impact") and hidden categories are dropped.
  const addInto = (
    acc: Record<string, number>,
    src: Record<string, CategoryBreakdown> | undefined,
  ) => {
    for (const [cat, b] of Object.entries(src || {})) {
      if (cat.includes("Total Impact") || isHiddenCategory(cat)) continue;
      acc[cat] = (acc[cat] || 0) + getValue(b, activeTab);
    }
  };
  const consumptionMap: Record<string, number> = {};
  addInto(consumptionMap, summary.grid_supply?.impacts);
  addInto(consumptionMap, summary.ng_impacts?.impacts);
  const offsetMap: Record<string, number> = {};
  addInto(offsetMap, summary.re_benefits?.impacts);
  const infraMap: Record<string, number> = {};
  addInto(infraMap, summary.re_infrastructure?.impacts);

  const usingGrid =
    !!summary.grid_supply?.impacts &&
    Object.keys(summary.grid_supply.impacts).length > 0;
  const sourceLabel = usingGrid ? "Grid + on-site supply" : "On-site natural gas";
  const hasOffset =
    Object.values(offsetMap).some((v) => Math.abs(v) > 0) ||
    Object.values(infraMap).some((v) => Math.abs(v) > 0);

  const entries = [
    ...new Set([
      ...Object.keys(consumptionMap),
      ...Object.keys(offsetMap),
      ...Object.keys(infraMap),
    ]),
  ]
    .map((category) => {
      const consumption = consumptionMap[category] || 0;
      const offset = offsetMap[category] || 0;
      const infra = infraMap[category] || 0;
      return { category, consumption, offset, infra, net: consumption - offset + infra };
    })
    .filter(
      (e) =>
        Math.abs(e.consumption) > 0 ||
        Math.abs(e.offset) > 0 ||
        Math.abs(e.infra) > 0,
    )
    .sort((a, b) => Math.abs(b.consumption) - Math.abs(a.consumption));

  // Chart shows the gross consumption footprint by category (>1% contributors).
  const totalMagnitude = entries.reduce((s, e) => s + Math.abs(e.consumption), 0);
  const chartEntries = entries.filter(
    (e) => totalMagnitude === 0 || Math.abs(e.consumption) / totalMagnitude > 0.01
  );

  const tabConfig = TAB_CONFIG[activeTab];

  const getChartData = (): ChartData<"bar"> => {
    const isEcon = activeTab === "economic";
    const values = chartEntries.map((e) =>
      isEcon ? e.consumption / 1_000_000 : e.consumption
    );

    return {
      labels: chartEntries.map((e) => getCategoryDisplayName(e.category)),
      datasets: [
        {
          label:
            activeTab === "economic"
              ? "Impact ($M)"
              : activeTab === "dalys"
              ? "Impact (DALYs)"
              : "Impact (PDFs)",
          data: values,
          backgroundColor: "#EF4444",
          barThickness: "flex" as const,
          barPercentage: 0.8,
        },
      ],
    };
  };

  const chartOptions: ChartOptions<"bar"> = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1E293B",
        titleColor: "#F8FAFC",
        bodyColor: "#CBD5E1",
        borderColor: "#334155",
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const val = context.parsed.x;
            if (activeTab === "economic") return `$${toSigFigs(val)}M`;
            return toSigFigs(val);
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: "#334155" },
        ticks: {
          color: "#94A3B8",
          callback: (tickValue) => {
            const value = Number(tickValue);
            if (activeTab === "economic") return `$${toSigFigs(value)}M`;
            return toSigFigs(value);
          },
        },
      },
      y: {
        grid: { display: false },
        ticks: { color: "#CBD5E1" },
      },
    },
  };

  const hasData = chartEntries.length > 0;

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      {/* Header row with title + badge */}
      <div className="flex items-center justify-between px-6 pt-5 pb-2">
        <h2 className="text-lg font-medium text-text-primary">
          Impact Explorer
          <span className="block text-xs font-normal text-text-muted mt-0.5">
            {sourceLabel} &middot; full ReCiPe/USEtox — air, soil &amp; water
          </span>
        </h2>
        <span className="text-xs text-red-400/70 bg-red-500/10 px-2 py-0.5 rounded">
          Increases in impact
        </span>
      </div>

      {/* Tab buttons — at the top */}
      <div className="flex gap-2 px-6 pb-3">
        {(Object.keys(TAB_CONFIG) as MetricTab[]).map((tab) => {
          const cfg = TAB_CONFIG[tab];
          const Icon = cfg.icon;
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                isActive
                  ? `${cfg.activeColor} border-current`
                  : `text-text-muted border-border ${cfg.hoverColor}`
              }`}
            >
              <Icon size={15} />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Description */}
      <div className="px-6 pb-2">
        <p className="text-sm text-text-secondary">{tabConfig.description}</p>
      </div>

      {/* Chart */}
      <div className="px-6">
        <div className="h-80 w-full">
          {hasData ? (
            <Bar data={getChartData()} options={chartOptions} height={300} />
          ) : (
            <div className="h-full flex items-center justify-center text-text-muted text-sm">
              No categorized impact data available. Re-run the assessment to
              generate breakdowns.
            </div>
          )}
        </div>
      </div>

      {/* Full category table */}
      {entries.length > 0 && (
        <div className="px-6 pb-5 mt-2">
          <h3 className="text-sm font-medium text-text-muted mb-2">
            All Categories ({tabConfig.unit}) — consumption, on-site RE offset &amp; net
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-600">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                    Consumption
                  </th>
                  {hasOffset && (
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                      RE offset
                    </th>
                  )}
                  {hasOffset && (
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                      RE infra
                    </th>
                  )}
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                    Net
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.map((e) => (
                  <tr
                    key={e.category}
                    className="hover:bg-surface-600 transition-colors"
                  >
                    <td className="px-4 py-2 text-sm text-text-secondary">
                      {getCategoryDisplayName(e.category)}
                    </td>
                    <td className="px-4 py-2 text-sm text-text-secondary text-right font-medium whitespace-nowrap">
                      {formatTableValue(e.consumption, activeTab)}
                    </td>
                    {hasOffset && (
                      <td className="px-4 py-2 text-sm text-right whitespace-nowrap text-emerald-400">
                        {Math.abs(e.offset) > 0
                          ? `−${formatTableValue(Math.abs(e.offset), activeTab)}`
                          : "—"}
                      </td>
                    )}
                    {hasOffset && (
                      <td className="px-4 py-2 text-sm text-right whitespace-nowrap text-text-muted">
                        {Math.abs(e.infra) > 0
                          ? `+${formatTableValue(Math.abs(e.infra), activeTab)}`
                          : "—"}
                      </td>
                    )}
                    <td className="px-4 py-2 text-sm text-right font-semibold whitespace-nowrap text-text-primary">
                      {formatTableValue(e.net, activeTab)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
